var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useRef, useState } from "react";
import { downloadReportJobFile, fetchReportJob } from "../api/reportsService";
import { isReportJobActive, } from "../types/reports/reportJob";
/** Delay between two status polls (after the previous answer arrived). */
export var REPORT_JOB_POLL_INTERVAL_MS = 2000;
/** Consecutive failed polls after which the job state is reported as unknown. */
var MAX_CONSECUTIVE_POLL_ERRORS = 3;
var STORAGE_KEY_PREFIX = "br-publishers.report-job.";
/** The job no longer exists (24 h cleanup) — it is forgotten silently. */
var JOB_GONE_STATUS = 404;
/** The job belongs to somebody else / the user lost the permission — shown as an error. */
var JOB_FORBIDDEN_STATUS = 403;
/**
 * sessionStorage key of the last job of one report (e.g. `kult-18-01.<publisherId>`).
 *
 * @param jobKey report + scope key
 * @returns the storage key
 */
export var reportJobStorageKey = function (jobKey) {
    return "".concat(STORAGE_KEY_PREFIX).concat(jobKey);
};
var readStoredJobId = function (jobKey) {
    try {
        return sessionStorage.getItem(reportJobStorageKey(jobKey));
    }
    catch (_a) {
        return null;
    }
};
var storeJobId = function (jobKey, jobId) {
    try {
        if (jobId) {
            sessionStorage.setItem(reportJobStorageKey(jobKey), jobId);
        }
        else {
            sessionStorage.removeItem(reportJobStorageKey(jobKey));
        }
    }
    catch (_a) {
        // storage unavailable (private mode) — the job is just not resumable
    }
};
var responseStatus = function (error) {
    var _a;
    return typeof error === "object" && error !== null
        ? (_a = error.response) === null || _a === void 0 ? void 0 : _a.status
        : undefined;
};
/**
 * Tracks one asynchronously generated agency report (DB-876).
 *
 * After `start` the job status is polled: the first request goes out at once, every next one
 * {@link REPORT_JOB_POLL_INTERVAL_MS} ms after the previous answer (never two in flight). Polling stops on
 * DONE / FAILED, on unmount (the request is aborted) and after three consecutive failures (`pollFailed`).
 * The job id is kept in sessionStorage per `jobKey`, so a user who leaves and comes back still sees the running
 * job and can download the finished file; a stored job answered with 404 (expired) is silently forgotten, a 403 is
 * reported (`accessDenied`).
 *
 * @param jobKey report + scope key (one job per key)
 * @param ky okapi ky instance; its identity may change on every render
 */
export var useReportJob = function (jobKey, ky) {
    var kyRef = useRef(ky);
    kyRef.current = ky;
    var _a = useState(function () {
        return readStoredJobId(jobKey);
    }), jobId = _a[0], setJobId = _a[1];
    var _b = useState(null), job = _b[0], setJob = _b[1];
    var _c = useState(false), pollFailed = _c[0], setPollFailed = _c[1];
    var _d = useState(false), accessDenied = _d[0], setAccessDenied = _d[1];
    // the scope changed (another publisher) — show that scope's own job
    useEffect(function () {
        setJobId(readStoredJobId(jobKey));
        setJob(null);
        setPollFailed(false);
        setAccessDenied(false);
    }, [jobKey]);
    var start = useCallback(function (started) {
        storeJobId(jobKey, started.jobId);
        setPollFailed(false);
        setAccessDenied(false);
        setJob({ jobId: started.jobId, status: started.status });
        setJobId(started.jobId);
    }, [jobKey]);
    var reset = useCallback(function () {
        storeJobId(jobKey, null);
        setPollFailed(false);
        setAccessDenied(false);
        setJob(null);
        setJobId(null);
    }, [jobKey]);
    useEffect(function () {
        if (!jobId) {
            return undefined;
        }
        var cancelled = false;
        var timer = null;
        var controller = null;
        var consecutiveErrors = 0;
        var poll = function () { return __awaiter(void 0, void 0, void 0, function () {
            var data, error_1, status_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        controller = new AbortController();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, fetchReportJob(kyRef.current, jobId, controller.signal)];
                    case 2:
                        data = _a.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        consecutiveErrors = 0;
                        setJob(data);
                        if (isReportJobActive(data.status)) {
                            timer = setTimeout(poll, REPORT_JOB_POLL_INTERVAL_MS);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        if (cancelled)
                            return [2 /*return*/];
                        status_1 = responseStatus(error_1);
                        if (status_1 === JOB_GONE_STATUS) {
                            storeJobId(jobKey, null);
                            setJob(null);
                            setJobId(null);
                            return [2 /*return*/];
                        }
                        if (status_1 === JOB_FORBIDDEN_STATUS) {
                            setAccessDenied(true);
                            return [2 /*return*/];
                        }
                        consecutiveErrors += 1;
                        if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                            console.error("Error polling report job status:", error_1);
                            setPollFailed(true);
                            return [2 /*return*/];
                        }
                        timer = setTimeout(poll, REPORT_JOB_POLL_INTERVAL_MS);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        poll();
        return function () {
            cancelled = true;
            if (timer)
                clearTimeout(timer);
            controller === null || controller === void 0 ? void 0 : controller.abort();
        };
    }, [jobId, jobKey]);
    var download = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!jobId)
                        return [2 /*return*/];
                    return [4 /*yield*/, downloadReportJobFile(kyRef.current, jobId, job === null || job === void 0 ? void 0 : job.fileName)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [jobId, job === null || job === void 0 ? void 0 : job.fileName]);
    var currentJob = job && job.jobId === jobId ? job : null;
    var status = currentJob === null || currentJob === void 0 ? void 0 : currentJob.status;
    return {
        jobId: jobId,
        job: currentJob,
        /** A job exists and has not finished yet (also while its first status is loading). */
        isGenerating: !!jobId &&
            !pollFailed &&
            !accessDenied &&
            (!currentJob || isReportJobActive(status)),
        isDone: status === "DONE",
        isFailed: status === "FAILED",
        pollFailed: pollFailed,
        /** The server refused the job (403) — show an error instead of silently dropping it. */
        accessDenied: accessDenied,
        start: start,
        reset: reset,
        download: download,
    };
};
